/**
 * The brand's existing logo, read from the casino instance's `casino_data.details.logo`, which is either a
 * plain URL or a map of slot → `{ src }`. Shared by the Creatives page (the storyboard brand identity and the
 * custom studio's logo set) and the Art Style page (its read-only brand logo card).
 */
import { PRIMARY_SLOT } from '../casino-settings/logo-api';

/** Placement slots that are app chrome, not distinct brand marks: excluded from the "+" logo set. */
export const LOGO_CHROME_SLOTS = new Set(['topNavDesktop', 'topNavMobile', 'onboarding', 'footer', 'loaderImage']);

/** A single brand-logo url from the casino-instance `details.logo`, which is either a string or a map
 *  of slot → { src }. Picks the first non-chrome variant. Used for the storyboard brand identity (the
 *  canvas Brand card AND the logo baked into the generated ad). */
export const firstBrandLogoUrl = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') return raw || undefined;
  if (!raw || typeof raw !== 'object') return undefined;
  for (const [slot, asset] of Object.entries(raw as Record<string, { src?: string } | undefined>)) {
    if (LOGO_CHROME_SLOTS.has(slot)) continue;
    const src = asset?.src;
    if (typeof src === 'string' && src) return src;
  }
  return undefined;
};

/** Like {@link firstBrandLogoUrl}, but the primary logo (symbol + name, horizontal) wins when the map has it,
 *  so a style is drawn against the brand's main mark rather than whichever variant happens to come first. */
export const primaryBrandLogoUrl = (raw: unknown): string | undefined => {
  if (raw && typeof raw === 'object') {
    const primary = (raw as Record<string, { src?: unknown } | undefined>)[PRIMARY_SLOT]?.src;
    if (typeof primary === 'string' && primary) return primary;
  }
  return firstBrandLogoUrl(raw);
};
